import { useEffect, useState } from 'react'
import useApi from '../hooks/useApi'
import { formatControlRequestStatus, getCSRFToken } from '../lib/utils'
import { DelegatedAccessLevel, formatDelegatedAccessLevel } from '../lib/delegatedAccess'
import { Button, LoadingSpinner } from './BaseComponents'
import CanonicalModal from './BaseComponents/CanonicalModal/CanonicalModal'
import SimpleSortTable, { TableColumn } from './BaseComponents/SimpleSortTable/SimpleSortTable'
import DelegatedAccessLevelModal from './DelegatedAccessLevelModal'

type Grant = {
	_id: string
	id: string
	status: string
	accessLevel: DelegatedAccessLevel
	delegateUserName?: string
	delegateCompanyName?: string
}

type Action = { kind: 'approve' | 'changeLevel' | 'decline' | 'revoke'; grant: Grant }

function AdvisorsTable() {
	const [loading, setLoading] = useState(false)
	const [data, setData] = useState<Grant[]>([])
	const [action, setAction] = useState<Action | null>(null)
	const [saving, setSaving] = useState(false)
	const api = useApi()

	const loadData = async () => {
		setLoading(true)
		const result = await api.get('/delegatedAccess/forMyOrganisation?limit=100')
		setData(result.data.map((grant: Omit<Grant, '_id'>) => ({ ...grant, _id: grant.id })))
		setLoading(false)
	}

	useEffect(() => {
		loadData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const run = async (request: () => Promise<unknown>) => {
		setSaving(true)
		try {
			await request()
		} finally {
			setSaving(false)
			setAction(null)
		}
		loadData()
	}

	const approve = (grant: Grant, level: DelegatedAccessLevel) =>
		run(async () =>
			api.post(`delegatedAccess/${grant.id}/decide`, { decision: 'approve', level, _csrf: await getCSRFToken() })
		)

	const changeLevel = (grant: Grant, level: DelegatedAccessLevel) =>
		run(async () => api.put(`delegatedAccess/${grant.id}/level`, { level, _csrf: await getCSRFToken() }))

	const decline = (grant: Grant) =>
		run(async () =>
			api.post(`delegatedAccess/${grant.id}/decide`, { decision: 'reject', _csrf: await getCSRFToken() })
		)

	const revoke = (grant: Grant) =>
		run(async () => api.delete(`delegatedAccess/${grant.id}`, { _csrf: await getCSRFToken() }))

	if (loading) {
		return <LoadingSpinner />
	}

	if (!data.length) {
		return <div className='text-white px-4 py-2 mt-2'>No advisors have requested access to your organisation</div>
	}

	const columns: TableColumn[] = [
		{ id: 'delegateUserName', label: 'Advisor' },
		{ id: 'delegateCompanyName', label: 'Organisation name' },
		{
			id: 'status',
			label: 'Status',
			renderer: (key, row) => <span>{formatControlRequestStatus(row[key])}</span>,
		},
		{
			id: 'accessLevel',
			label: 'Access level',
			renderer: (key, row) => <span>{row.status === 'active' ? formatDelegatedAccessLevel(row[key]) : ''}</span>,
		},
		{
			id: 'actions',
			label: 'Actions',
			textAlign: 'center',
			renderer: (key, row) => {
				const grant = row as Grant
				return (
					<div className='flex space-x-2 justify-center'>
						{grant.status === 'pending' && (
							<>
								<Button
									type='Primary'
									size='sm'
									isTableRowButton
									text='Approve'
									onClick={() => setAction({ kind: 'approve', grant })}
								/>
								<Button
									type='Primary'
									size='sm'
									isTableRowButton
									text='Decline'
									onClick={() => setAction({ kind: 'decline', grant })}
								/>
							</>
						)}
						{grant.status === 'active' && (
							<>
								<Button
									type='Primary'
									size='sm'
									isTableRowButton
									text='Change access'
									onClick={() => setAction({ kind: 'changeLevel', grant })}
								/>
								<Button
									type='Primary'
									size='sm'
									isTableRowButton
									text='Revoke access'
									onClick={() => setAction({ kind: 'revoke', grant })}
								/>
							</>
						)}
					</div>
				)
			},
		},
	]

	const advisorName = action?.grant.delegateUserName ?? ''
	const levelModalKind = action?.kind === 'approve' || action?.kind === 'changeLevel' ? action.kind : null
	const confirmKind = action?.kind === 'decline' || action?.kind === 'revoke' ? action.kind : null

	return (
		<>
			<SimpleSortTable data={data} columns={columns} />
			<DelegatedAccessLevelModal
				open={!!levelModalKind}
				title={levelModalKind === 'approve' ? 'Approve access request' : 'Change access level'}
				subtitle={advisorName}
				confirmText={levelModalKind === 'approve' ? 'Approve' : 'Save'}
				initialLevel={action?.grant.accessLevel ?? 'view'}
				loading={saving}
				onClose={() => setAction(null)}
				onConfirm={(level) =>
					action && (levelModalKind === 'approve' ? approve(action.grant, level) : changeLevel(action.grant, level))
				}
			/>
			<CanonicalModal
				open={!!confirmKind}
				onClose={() => setAction(null)}
				title={confirmKind === 'revoke' ? 'Revoke access' : 'Decline access request'}
				subtitle={advisorName}
				actions={
					<>
						<Button
							type='Primary'
							size='sm'
							text={confirmKind === 'revoke' ? 'Revoke access' : 'Decline'}
							disabled={saving}
							onClick={() => action && (confirmKind === 'revoke' ? revoke(action.grant) : decline(action.grant))}
							additionalClass='mr-5'
						/>
						<Button type='Outlined' size='sm' text='Cancel' onClick={() => setAction(null)} />
					</>
				}
			>
				<p className='text-white mt-8'>
					{confirmKind === 'revoke'
						? 'This advisor will immediately lose access to your organisation.'
						: 'This advisor will not get access to your organisation.'}
				</p>
			</CanonicalModal>
		</>
	)
}

export default AdvisorsTable
